import { Component, Element, h, Host, Prop, State } from "@stencil/core";

import { getTheme, hasSlot, setPropsIfNull } from "../../../utils/utils";
import { addMediaChangeListener, media, removeMediaChangeListener } from "../../../utils/media-utils";

/**
 * @slot heading - Slot for `stzh-heading` element
 * @slot image - Slot for `stzh-figure` or `img` element
 * @slot subheading - Slot for `stzh-text` element
 * @slot readspeaker - Slot for `stzh-readspeaker` element
 */
@Component({
  tag: "stzh-pagetitle-home",
  styleUrl: "stzh-pagetitle-home.scss",
  scoped: true,
})
export class StzhPagetitleHome {
  /** Automatic Hyphens */
  @Prop({ reflect: true }) hyphensEnabled: boolean = false;

  @Element() element: HTMLStzhPagetitleHomeElement;

  @State() isSmall: boolean;

  private mediaChangeHandler = () => {
    this.isSmall = media("small").matches;
  };

  private init = () => {
    const heading = this.element.querySelector('stzh-heading[slot="heading"], [slot="heading"] stzh-heading');
    const subheading = this.element.querySelector('stzh-text[slot="subheading"], [slot="subheading"] stzh-text');
    const readspeakerButton = this.element.querySelector('[slot="readspeaker"] stzh-button');
    const theme = getTheme();

    // Base config
    let headingCurve = "h1home";
    let subheadingCurve = "hero";

    switch (theme) {
      case "stzh-theme-pkzh":
        headingCurve = "h1homesmall";
        subheadingCurve = "h2";
        break;
    }

    if (heading) {
      setPropsIfNull(heading, {
        curve: headingCurve,
        level: "1",
      } as HTMLStzhHeadingElement);
    }

    if (readspeakerButton) {
      setPropsIfNull(readspeakerButton, {
        variant: "tertiary-plain",
        noPaddingLeft: true,
        noPaddingRight: true,
        size: "small",
        sizeMedium: "tiny",
        buttonAccesskey: "L",
      } as HTMLStzhButtonElement);
    }

    if (subheading) {
      setPropsIfNull(subheading, {
        curve: subheadingCurve,
        lead: true,
      } as HTMLStzhTextElement);
    }
  };

  componentDidRender() {}

  connectedCallback() {
    this.init();
    this.mediaChangeHandler();
    addMediaChangeListener(this.mediaChangeHandler);
  }

  disconnectedCallback() {
    removeMediaChangeListener(this.mediaChangeHandler);
  }

  render() {
    const imageSlotUsed = hasSlot(this.element, "image");
    const subheadingSlotUsed = hasSlot(this.element, "subheading");
    const readspeakerSlotUsed = hasSlot(this.element, "readspeaker");

    const classes = {
      "stzh-pagetitle-home": true,
      "stzh-pagetitle-home--has-image": imageSlotUsed,
      "stzh-pagetitle-home--has-no-image": !imageSlotUsed,
      "stzh-pagetitle-home--has-subheading": subheadingSlotUsed,
      "stzh-pagetitle-home--hyphens": this.hyphensEnabled,
      "stzh-pagetitle-home--has-meta": readspeakerSlotUsed,
    };

    return (
      <Host has-image={imageSlotUsed}>
        <div class={classes}>
          <div class="stzh-pagetitle-home__image-wrapper">
            <stzh-ratio
              ratio={"1:1"}
              ratioSmall={"16:9"}
              ratioMedium={"2:1"}
              ratioLarge={"21:9"}
              class="stzh-pagetitle-home__ratio"
            ></stzh-ratio>
            <div class="stzh-pagetitle-home__image-gradient"></div>
            <div class="stzh-pagetitle-home__image-inner-wrapper">
              <slot name="image"></slot>
            </div>
          </div>
          <div class="stzh-pagetitle-home__container">
            <div class="stzh-pagetitle-home__wrapper">
              <div class="stzh-pagetitle-home__text">
                <div class="stzh-pagetitle-home__meta">
                  <div class="stzh-pagetitle-home__readspeaker">
                    <slot name="readspeaker"></slot>
                  </div>
                </div>

                <div class="stzh-pagetitle-home__heading">
                  <slot name="heading"></slot>
                </div>

                <div class="stzh-pagetitle-home__subheading">
                  <slot name="subheading"></slot>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
